import React, { useState, useEffect } from 'react';
import { useWizard } from '../../context/WizardContext';
import NavigationButtons from '../shared/NavigationButtons';
import ExplanationBox from '../shared/ExplanationBox';
import GuardrailWarning from '../shared/GuardrailWarning';
import GlossaryTerm from '../Glossary/GlossaryTerm';

const Step5_TimeHorizon = () => {
  const { dataA, updateDataRaw, nextStep, logDecision } = useWizard();
  const [selectedHorizon, setSelectedHorizon] = useState(dataA.timeHorizon || '');
  const [horizonDetails, setHorizonDetails] = useState(dataA.timeHorizonDetails || {});
  const [warnings, setWarnings] = useState([]);

  useEffect(() => {
    if (dataA.timeHorizon) {
      setSelectedHorizon(dataA.timeHorizon);
    }
    if (dataA.timeHorizonDetails) {
      setHorizonDetails(dataA.timeHorizonDetails);
    }
  }, [dataA.timeHorizon, dataA.timeHorizonDetails]);

  const horizonOptions = [
    {
      value: 'crossSectional',
      label: 'Cross-Sectional',
      description: 'Data collected at a single point in time - a snapshot view',
      pros: 'Quick, cost-effective, efficient for describing current state',
      cons: 'Cannot establish temporal precedence, cannot track change',
      bestFor: 'Describing current relationships, prevalence studies, limited time/resources',
      glossaryKey: 'crossSectional'
    },
    {
      value: 'longitudinal',
      label: 'Longitudinal',
      description: 'Data collected at multiple time points to track changes over time',
      pros: 'Can establish temporal order, track development/change, test causal hypotheses',
      cons: 'Expensive, time-consuming, attrition issues, requires sustained commitment',
      bestFor: 'Studying change, development, causal processes, long-term effects',
      glossaryKey: 'longitudinal'
    },
    {
      value: 'retrospective',
      label: 'Retrospective',
      description: 'Looking backward - using existing records or participant recall of past events',
      pros: 'Immediate access to "past" data, efficient, no waiting',
      cons: 'Relies on memory/records (can be inaccurate), limited to existing data, recall bias',
      bestFor: 'Historical analysis, exploratory research, when prospective study is not feasible',
      glossaryKey: 'retrospective'
    },
    {
      value: 'crossSequential',
      label: 'Cross-Sequential',
      description: 'Hybrid design combining cross-sectional and longitudinal elements',
      pros: 'Separates age, cohort, and time effects; more efficient than pure longitudinal',
      cons: 'Complex design and analysis, still resource-intensive',
      bestFor: 'Developmental research, disentangling cohort effects from true change'
    }
  ];

  // Generate default recommendations based on context
  useEffect(() => {
    const newWarnings = [];
    
    // Time constraint alignment
    const timeConstraint = dataA.intake?.timeConstraints;
    if (timeConstraint === 'months' && selectedHorizon === 'longitudinal') {
      newWarnings.push({
        type: 'warning',
        message: 'You indicated tight time constraints. Longitudinal designs typically require extended timelines (years). Consider if accelerated longitudinal or cross-sectional design is more feasible.'
      });
    }

    // Cross-sectional causality warning
    if (selectedHorizon === 'crossSectional' && dataA.theoryApproach === 'deductive' && dataA.strategy === 'experiment') {
      newWarnings.push({
        type: 'warning',
        message: 'Cross-sectional experiments can test causality within the experimental session, but cannot track long-term effects. Consider follow-up measurements if interested in sustained effects.'
      });
    }

    // Longitudinal requirements
    if (selectedHorizon === 'longitudinal') {
      if (!horizonDetails.timePoints || horizonDetails.timePoints < 2) {
        newWarnings.push({
          type: 'error',
          message: 'Longitudinal designs require at least 2 time points. Specify how many waves of data collection.'
        });
      }
      
      if (!horizonDetails.interval) {
        newWarnings.push({
          type: 'warning',
          message: 'Specify the interval between measurements (e.g., 6 months, 1 year). This should align with the expected rate of change in your phenomenon.'
        });
      }

      if (horizonDetails.duration && parseFloat(horizonDetails.duration) > 5 && !horizonDetails.attritionPlan) {
        newWarnings.push({
          type: 'warning',
          message: 'Long-term longitudinal studies (>5 years) experience significant participant attrition. Plan retention strategies and consider intent-to-treat analysis.'
        });
      }
    }

    // Retrospective data quality
    if (selectedHorizon === 'retrospective') {
      if (!horizonDetails.dataSource) {
        newWarnings.push({
          type: 'warning',
          message: 'Specify your retrospective data source (e.g., medical records, administrative data, participant recall). Data quality varies significantly by source.'
        });
      }

      if (horizonDetails.dataSource === 'recall' && !horizonDetails.recallPeriod) {
        newWarnings.push({
          type: 'warning',
          message: 'When relying on participant recall, specify the recall period. Accuracy decreases significantly beyond 6-12 months for most events.'
        });
      }
    }

    // Strategy alignment
    if (dataA.strategy === 'ethnography' && selectedHorizon === 'crossSectional') {
      newWarnings.push({
        type: 'warning',
        message: 'Ethnography typically requires extended immersion (longitudinal). A single snapshot may not capture cultural patterns adequately.'
      });
    }

    if (dataA.strategy === 'groundedTheory' && selectedHorizon === 'retrospective') {
      newWarnings.push({
        type: 'warning',
        message: 'Grounded theory often requires iterative data collection (longitudinal process). Retrospective data limits your ability to pursue theoretical sampling.'
      });
    }

    setWarnings(newWarnings);
  }, [selectedHorizon, horizonDetails, dataA]);

  const handleHorizonChange = (value) => {
    setSelectedHorizon(value);
    setHorizonDetails({}); // Reset details when changing horizon
  };

  const handleDetailsChange = (key, value) => {
    setHorizonDetails(prev => ({ ...prev, [key]: value }));
  };

  const canProceed = () => {
    if (!selectedHorizon) return false;
    
    // Block on critical errors
    const hasBlockingErrors = warnings.some(w => w.type === 'error');
    if (hasBlockingErrors) return false;

    // Longitudinal specific requirements
    if (selectedHorizon === 'longitudinal' && (!horizonDetails.timePoints || horizonDetails.timePoints < 2)) {
      return false;
    }

    return true;
  };

  const handleNext = () => {
    updateDataRaw({
      'wizardA.timeHorizon': selectedHorizon,
      'wizardA.timeHorizonDetails': horizonDetails
    });

    const selectedOption = horizonOptions.find(h => h.value === selectedHorizon);
    logDecision(
      'Time Horizon',
      selectedHorizon,
      `Selected ${selectedOption?.label}. ${Object.entries(horizonDetails).map(([k, v]) => `${k}: ${v}`).join(', ')}`
    );

    nextStep();
  };

  const selectedHorizonInfo = horizonOptions.find(h => h.value === selectedHorizon);

  return (
    <div className="wizard-step">
      <h2>Step 7: Time Horizon</h2>
      <p className="step-description">
        Define the temporal dimension of your study - will you capture a snapshot in time, track changes over time, or look backward?
      </p>

      <ExplanationBox title="Why does time horizon matter?">
        <p>
          The time horizon determines whether you can establish temporal precedence (X precedes Y), 
          track development and change, or simply describe relationships at one point in time.
        </p>
        <p><strong>Your context:</strong></p>
        <ul>
          <li>Time constraints: {dataA.intake?.timeConstraints || 'Not specified'}</li>
          <li>Research strategy: {dataA.strategy || 'Not selected'}</li>
          <li>Theory approach: {dataA.theoryApproach || 'Not selected'}</li>
        </ul>
      </ExplanationBox>

      <div className="form-section">
        <h3>Select Time Horizon</h3>
        
        <div className="horizon-grid">
          {horizonOptions.map(horizon => (
            <div
              key={horizon.value}
              className={`horizon-card ${selectedHorizon === horizon.value ? 'selected' : ''}`}
              onClick={() => handleHorizonChange(horizon.value)}
            >
              <div className="horizon-header">
                <input
                  type="radio"
                  name="timeHorizon"
                  value={horizon.value}
                  checked={selectedHorizon === horizon.value}
                  onChange={(e) => handleHorizonChange(e.target.value)}
                />
                <strong>{horizon.label}</strong>
                {horizon.glossaryKey && <GlossaryTerm termKey={horizon.glossaryKey} />}
              </div>
              <p className="horizon-description">{horizon.description}</p>
              <div className="horizon-meta">
                <div className="pros-cons">
                  <div>
                    <small><strong>Pros:</strong> {horizon.pros}</small>
                  </div>
                  <div>
                    <small><strong>Cons:</strong> {horizon.cons}</small>
                  </div>
                </div>
                <small className="best-for"><strong>Best for:</strong> {horizon.bestFor}</small>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Time horizon specific details */}
      {selectedHorizon === 'longitudinal' && (
        <div className="form-section">
          <h3>Longitudinal Design Details</h3>
          
          <div className="form-group">
            <label>Number of Time Points (Waves) <GlossaryTerm termKey="longitudinal" /></label>
            <input
              type="number"
              min="2"
              value={horizonDetails.timePoints || ''}
              onChange={(e) => handleDetailsChange('timePoints', e.target.value)}
              placeholder="e.g., 3, 4, 5"
              required
            />
            <small>Minimum 2 required. More waves = better tracking of change trajectories</small>
          </div>

          <div className="form-group">
            <label>Interval Between Measurements</label>
            <select
              value={horizonDetails.interval || ''}
              onChange={(e) => handleDetailsChange('interval', e.target.value)}
            >
              <option value="">Select interval...</option>
              <option value="weeks">Weeks</option>
              <option value="months">Months (common for most studies)</option>
              <option value="years">Years (developmental studies)</option>
              <option value="decades">Decades (long-term cohort studies)</option>
            </select>
            <small>Choose based on expected rate of change in your variables</small>
          </div>

          <div className="form-group">
            <label>Total Study Duration</label>
            <input
              type="text"
              value={horizonDetails.duration || ''}
              onChange={(e) => handleDetailsChange('duration', e.target.value)}
              placeholder="e.g., 2 years, 5 years"
            />
          </div>

          <div className="form-group">
            <label>Longitudinal Design Type</label>
            <select
              value={horizonDetails.longType || ''}
              onChange={(e) => handleDetailsChange('longType', e.target.value)}
            >
              <option value="">Select type...</option>
              <option value="panel">Panel Study (same participants over time)</option>
              <option value="cohort">Cohort Study (specific cohort followed over time)</option>
              <option value="trend">Trend Study (different samples from population over time)</option>
            </select>
          </div>

          {parseFloat(horizonDetails.duration) > 3 && (
            <div className="form-group">
              <label>Attrition Mitigation Plan</label>
              <textarea
                value={horizonDetails.attritionPlan || ''}
                onChange={(e) => handleDetailsChange('attritionPlan', e.target.value)}
                placeholder="How will you minimize participant dropout? (e.g., incentives, regular contact, flexible scheduling)"
                rows="3"
              />
            </div>
          )}
        </div>
      )}

      {selectedHorizon === 'retrospective' && (
        <div className="form-section">
          <h3>Retrospective Study Details</h3>
          
          <div className="form-group">
            <label>Data Source <GlossaryTerm termKey="retrospective" /></label>
            <select
              value={horizonDetails.dataSource || ''}
              onChange={(e) => handleDetailsChange('dataSource', e.target.value)}
            >
              <option value="">Select source...</option>
              <option value="records">Existing Records/Archives (medical, administrative, etc.)</option>
              <option value="recall">Participant Recall (retrospective interviews/surveys)</option>
              <option value="secondary">Secondary Data (previously collected datasets)</option>
              <option value="mixed">Mixed Sources</option>
            </select>
          </div>

          {horizonDetails.dataSource === 'recall' && (
            <div className="form-group">
              <label>Recall Period</label>
              <input
                type="text"
                value={horizonDetails.recallPeriod || ''}
                onChange={(e) => handleDetailsChange('recallPeriod', e.target.value)}
                placeholder="e.g., past 6 months, past 5 years"
              />
              <small>⚠️ Recall accuracy decreases with time. Consider strategies to enhance accuracy (calendars, landmark events)</small>
            </div>
          )}

          <div className="form-group">
            <label>Time Frame of Interest</label>
            <input
              type="text"
              value={horizonDetails.retrospectiveTimeFrame || ''}
              onChange={(e) => handleDetailsChange('retrospectiveTimeFrame', e.target.value)}
              placeholder="e.g., 2015-2020, last decade"
            />
          </div>

          {horizonDetails.dataSource === 'records' && (
            <div className="form-group">
              <label>Data Availability Confirmed?</label>
              <select
                value={horizonDetails.dataConfirmed || ''}
                onChange={(e) => handleDetailsChange('dataConfirmed', e.target.value)}
              >
                <option value="">Select...</option>
                <option value="yes">Yes - access secured and data quality verified</option>
                <option value="pending">Pending - access in progress</option>
                <option value="uncertain">Uncertain - need to verify availability</option>
              </select>
            </div>
          )}
        </div>
      )}

      {selectedHorizon === 'crossSequential' && (
        <div className="form-section">
          <h3>Cross-Sequential Design Details</h3>
          
          <div className="form-group">
            <label>Number of Cohorts</label>
            <input
              type="number"
              min="2"
              value={horizonDetails.cohorts || ''}
              onChange={(e) => handleDetailsChange('cohorts', e.target.value)}
              placeholder="e.g., 3"
            />
            <small>Multiple cohorts measured longitudinally to separate age, cohort, and period effects</small>
          </div>

          <div className="form-group">
            <label>Measurement Occasions per Cohort</label>
            <input
              type="number"
              min="2"
              value={horizonDetails.occasions || ''}
              onChange={(e) => handleDetailsChange('occasions', e.target.value)}
              placeholder="e.g., 3"
            />
          </div>
        </div>
      )}

      {/* Warnings and Guardrails */}
      {warnings.length > 0 && (
        <div className="warnings-section">
          {warnings.map((warning, idx) => (
            <GuardrailWarning 
              key={idx} 
              type={warning.type}
              title={warning.type === 'error' ? 'Required' : 'Recommendation'}
            >
              {warning.message}
            </GuardrailWarning>
          ))}
        </div>
      )}

      {selectedHorizonInfo && (
        <ExplanationBox title="Alignment Check">
          <p>
            <strong>{selectedHorizonInfo.label}</strong> is appropriate when: {selectedHorizonInfo.bestFor}
          </p>
          <p><strong>Trade-offs:</strong></p>
          <ul>
            <li>Advantages: {selectedHorizonInfo.pros}</li>
            <li>Limitations: {selectedHorizonInfo.cons}</li>
          </ul>
        </ExplanationBox>
      )}

      <NavigationButtons
        onNext={handleNext}
        canProceed={true}
        nextLabel="Continue to Techniques"
      />
    </div>
  );
};

export default Step5_TimeHorizon;
